import { Button, TextField, Typography } from '@mui/material';
import { Autocomplete } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matAutocompleteStyle } from '../../assets/style/material/matSyles';
import { dates } from '../../constants/dates';
import { SdoAdminRequest } from '../../models/SdoInterface';
import { useSdoAdminEligibilityCriteriaProvider } from '../../providers/SdoAdminEligibilityCriteriaProvider';
import AppSelect from '../AppSelect/AppSelect';
import Loader from '../Loader/Loader';
import './SdoAdminManageRequests.scss';
import {
  DEFAULT_MANAGE_REQUESTS_ROWS,
  DEFAULT_MANAGE_REQUESTS_SEARCH_PARAMS,
  requestStatusType,
  sortDateComparator,
  sortSdoDateComparator,
  sortPilotNameComparator,
  sortStatusComparator,
  isPositionBaseEmpty,
  sortMonthYearComparator,
  sortDataRows
} from '../../pages/SdoAdmin/sdoAdminHelper';
import SdoRequestStatus from '../SdoRequestStatus/SdoRequestStatus';
import { Link } from 'react-router-dom';
import {
  getDateInWeekDayMonthDateFormat,
  convertToMonthName
} from '../../helpers/dateHelpers';
import {
  DataGridPro,
  GridColDef,
  GridSortModel,
  GridPagination
} from '@mui/x-data-grid-pro';
import {
  getNumDigits,
  initializeState,
  showAllRows
} from '../../helpers/viewAllHelper';

const SdoAdminManageRequests = ({
  searchState,
  setSearchState,
  setManagedPilotId,
  selectedPosition
}) => {
  const sdoAdminEligibilityCriteriaContext =
    useSdoAdminEligibilityCriteriaProvider();
  const { t } = useTranslation();
  const [rows, setRows] = useState(DEFAULT_MANAGE_REQUESTS_ROWS);
  const [pilotsAutocompleteList, setPilotsAutocompleteList] = useState<
    Array<string>
  >([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });
  const [searchParams, setSearchParams] = useState(
    searchState.defaultSearchState
      ? DEFAULT_MANAGE_REQUESTS_SEARCH_PARAMS
      : {
          pilotAutocompleteId: searchState.pilotAutocompleteId,
          pilotAutocompleteValue: searchState.pilotAutocompleteValue,
          sdoDate: isPositionBaseEmpty(selectedPosition)
            ? searchState.sdoDate
            : convertToMonthName(selectedPosition.month) +
              ' ' +
              selectedPosition.year,
          sdoPosition: isPositionBaseEmpty(selectedPosition)
            ? searchState.sdoPosition
            : selectedPosition.pos[0].base +
              '|' +
              selectedPosition.pos[0].equipment +
              '|' +
              selectedPosition.pos[0].rank,
          searchClicked: true
        }
  );
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const autocompleteStyle = matAutocompleteStyle();
  const selects = useRef([]);
  const [sdoDates, setSdoDates] = useState<Array<string>>([]);
  const [sdoPositions, setSdoPositions] = useState<Array<string>>([]);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [error, setError] = useState(false);
  const [autocompleteText, setAutocompleteText] = useState(
    searchState.defaultSearchState ? '' : searchState.pilotAutocompleteValue
  );
  const [viewAllClicked, setViewAllClicked] = useState(false);
  const [rowsPerPageOptions, setRowsPerPageOptions] = useState([25, 50, 100]);
  const [resetSelects, setResetSelects] = useState(0);

  const columns: GridColDef[] = [
    {
      field: 'position',
      headerName: 'Position(s)',
      width: 150
    },
    {
      field: 'pilot',
      headerName: 'Pilot',
      width: 200,
      sortComparator: sortPilotNameComparator,
      renderCell: (params: any) => {
        return params.value && params.value !== '-' ? (
          <div
            className="table__cell--pilot"
            style={{
              display:
                params.value.id.toString().length + params.value.name.length >
                16
                  ? 'block'
                  : 'flex'
            }}
          >
            <Typography className="pilot-lbl pilot-lbl--name">
              {params.value.name}
            </Typography>
            <Typography
              className={
                params.value.id.toString().length + params.value.name.length >
                16
                  ? 'pilot-lbl'
                  : 'pilot-lbl pilot-lbl--ml'
              }
            >
              (ID {params.value.id})
            </Typography>
          </div>
        ) : null;
      }
    },
    { field: 'seniority', headerName: 'Seniority', width: 140 },
    {
      field: 'additionalQualification',
      headerName: 'AQ',
      width: 90,
      renderCell: (params: any) => {
        return (
          <p className="sdo-manage-requests-table-cntr__add-qual">
            <span>
              {params.row.additionalQualification
                ? params.row.additionalQualification
                : ''}
            </span>
          </p>
        );
      }
    },
    rows[0]?.monthYear === '-'
      ? { field: 'monthYear', headerName: 'Year', width: 120 }
      : {
          field: 'monthYear',
          headerName: 'Year',
          width: 120,
          sortComparator: sortMonthYearComparator,
          renderCell: (params: any) => {
            return (
              <p className="sdo-manage-requests-table-cntr__year">
                <span>{params.value.year}</span>
              </p>
            );
          }
        },

    rows[0]?.datesRequested === '-'
      ? { field: 'datesRequested', headerName: 'Dates Requested', width: 300 }
      : {
          field: 'datesRequested',
          headerName: 'Dates Requested',
          width: 215,
          sortComparator: sortDateComparator,
          renderCell: (params: any) => {
            return (
              <p className="sdo-manage-requests-table-cntr__dates-requested">
                <span>
                  {params.value.startDate &&
                  params.value.endDate &&
                  params.value.days > 1
                    ? getDateInWeekDayMonthDateFormat(
                        new Date(params.value.startDate)
                      ) +
                      ' - ' +
                      getDateInWeekDayMonthDateFormat(
                        new Date(params.value.endDate)
                      ) +
                      ' '
                    : params.value.endDate
                      ? `${getDateInWeekDayMonthDateFormat(new Date(params.value.endDate))} ${' '} (${params.value.days} ${'day'})`
                      : ' '}
                </span>
                <span>
                  {params.value.days && params.value.days > 1
                    ? `${' '} (${params.value.days} ${'days'})`
                    : ''}
                </span>
              </p>
            );
          }
        },

    rows[0].status && rows[0].status !== '-'
      ? {
          field: 'status',
          headerName: 'Status',
          width: 485,
          renderCell: (params: any) => {
            return (
              <div className="table__cell--status">
                <SdoRequestStatus
                  status={params.value.label}
                  remarks={params.value.remarks}
                />
                <div
                  className="table__cell--manage__cntr"
                  onClick={() => handleManageClick(params.value.pilotId)}
                >
                  <i className="icon-manage_accounts_FILL0_wght400_GRAD0_opsz48-1-1 icon--x-large" />
                  <span className="manage-lbl">
                    {t(
                      'specialDayOff.adminEligibilityCriteria.manageRequests.manage'
                    )}
                  </span>
                </div>
              </div>
            );
          },
          sortComparator: sortStatusComparator
        }
      : { field: 'status', headerName: 'Status', flex: 1 }
  ];

  useEffect(() => {
    if (searchState.defaultSearchState || searchState.redirectAutoComplete) {
      sdoAdminEligibilityCriteriaContext.getPilotAutocomplete();
      sdoAdminEligibilityCriteriaContext.getSdoDates();
      sdoAdminEligibilityCriteriaContext.getSdoPositions();
    } else {
      handleSearchClick();
    }
    handleSortChange(searchState.sortModel);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sdoAdminEligibilityCriteriaContext.pilotAutocomplete) return;
    const pilotNameIds =
      sdoAdminEligibilityCriteriaContext.pilotAutocomplete.map((pilot) => {
        return `${pilot.NAME} (ID ${pilot.EMPLOYEE_ID})`;
      });
    setPilotsAutocompleteList(pilotNameIds);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdoAdminEligibilityCriteriaContext.pilotAutocomplete]);

  useEffect(() => {
    if (!sdoAdminEligibilityCriteriaContext.sdoDates.length) return;
    const formattedSdoDates = sdoAdminEligibilityCriteriaContext.sdoDates
      .sort(sortSdoDateComparator)
      .map((sdoDate) => {
        return `${sdoDate.sdoYear} ${dates[Number(sdoDate.sdoMonth) - 1]}`;
      });
    setSdoDates(['All', ...formattedSdoDates]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdoAdminEligibilityCriteriaContext.sdoDates]);

  useEffect(() => {
    if (!sdoAdminEligibilityCriteriaContext.sdoPositions.length) return;
    const positions = sdoAdminEligibilityCriteriaContext.sdoPositions.map(
      (position) => {
        return position.POSITION_GROUP;
      }
    );
    setSdoPositions(['All Positions', ...positions]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdoAdminEligibilityCriteriaContext.sdoPositions]);

  useEffect(() => {
    if (
      !sdoAdminEligibilityCriteriaContext.requestsData.length ||
      !searchParams.searchClicked
    ) {
      if (searchParams.searchClicked) setError(true);
      setRows(DEFAULT_MANAGE_REQUESTS_ROWS);
      return;
    }
    let requestDataRows: Array<SdoAdminRequest> = [];
    let uniqueId = 0;
    sdoAdminEligibilityCriteriaContext.requestsData.forEach((request) => {
      request.requestHistory.forEach((reqHist) => {
        if (reqHist.status !== requestStatusType.EXCEPTION) {
          requestDataRows.push({
            id: uniqueId,
            position: reqHist.position,
            additionalQualification: reqHist.aq !== 'null' ? reqHist.aq : '',
            pilot: {
              id: request.pilotId,
              name: request.pilotName
            },
            datesRequested: reqHist,
            monthYear: {
              date: new Date(reqHist.startDate).getDate(),
              month: new Date(reqHist.startDate).getMonth(),
              year: reqHist.year
            },
            seniority: reqHist.seniority,
            status: {
              label: reqHist.status,
              remarks: reqHist.remarks,
              pilotId: request.pilotId
            }
          });
          uniqueId += 1;
        }
      });
    });
    requestDataRows = requestDataRows.sort(sortDataRows);
    setError(false);
    setRows(requestDataRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdoAdminEligibilityCriteriaContext.requestsData]);

  useEffect(() => {
    setSortModel([
      {
        field: 'monthYear',
        sort: 'desc'
      }
    ]);
  }, [sdoAdminEligibilityCriteriaContext.requestsData]);

  useEffect(() => {
    initializeState(
      paginationModel,
      setPaginationModel,
      setRowsPerPageOptions,
      viewAllClicked,
      rows.length
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, viewAllClicked]);

  const handleAutocompleteOnChange = (event) => {
    setSearchParams({
      ...searchParams,
      sdoDate: 'All',
      sdoPosition: 'All Positions',
      pilotAutocompleteId: event.target.textContent.replace(/[^0-9]/g, ''),
      pilotAutocompleteValue: event.target.textContent
    });
    setAutocompleteText(event.target.textContent);
    setResetSelects((prev) => prev + 1);
  };

  const handleManageClick = (pilotId: string) => {
    setSearchState({
      defaultSearchState: false,
      pilotAutocompleteValue: searchParams.pilotAutocompleteValue,
      pilotAutocompleteId: searchParams.pilotAutocompleteId,
      sdoDate: selects.current['For SDO in'].querySelector('input').value,
      sdoPosition: selects.current['Position'].querySelector('input').value,
      sortModel: sortModel
    });
    setManagedPilotId(pilotId);
  };

  const handleSearchClick = () => {
    let month = selects.current['For SDO in'].querySelector('input').value;
    let position = selects.current['Position'].querySelector('input').value;
    let year: number;
    const allDates = month === 'All';
    if (month === 'All' || searchParams.pilotAutocompleteId) {
      month = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      year = currentYear - 3;
    } else {
      year = +month.split(' ')[0];
      month = dates.findIndex((date) => date === month.split(' ')[1]) + 1;
    }
    if (position !== 'All Positions') {
      const positionProps = position.replace(/\s/g, '').split('|');
      position = {
        base: positionProps[0],
        equipment: positionProps[1],
        rank: positionProps[2]
      };
    }
    setSearchParams({
      ...searchParams,
      sdoDate: selects.current['For SDO in'].querySelector('input').value,
      sdoPosition: selects.current['Position'].querySelector('input').value,
      searchClicked: true
    });
    setRows(DEFAULT_MANAGE_REQUESTS_ROWS);
    sdoAdminEligibilityCriteriaContext.getManageRequestsData(
      searchParams.pilotAutocompleteId,
      month,
      position,
      year,
      allDates
    );
  };

  useEffect(() => {
    if (
      !searchState.defaultSearchState &&
      selectedPosition.month &&
      selectedPosition.pos[0] &&
      selectedPosition.year
    ) {
      let position = selectedPosition.pos[0];
      if (isPositionBaseEmpty(selectedPosition)) position = 'All Positions';
      sdoAdminEligibilityCriteriaContext.getManageRequestsData(
        searchParams.pilotAutocompleteId,
        selectedPosition.month,
        position,
        selectedPosition.year
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClearClick = () => {
    // Clear location.state from ManageAll before reloading, else same data would load
    const updatedHistoryState = {
      ...window.history?.state?.state,
      isManageAllRedirect: false
    };
    window.history.replaceState(updatedHistoryState, '');
    window.location.reload();
  };

  const handleSortChange = (model: GridSortModel) => {
    /* prevent the infinite loop by confirming model is different than the current sortModel state when setting a sort field on page load, (fixed in subsequent DataGrid versions) */
    if (JSON.stringify(model) !== JSON.stringify(sortModel))
      setSortModel(model);
  };

  const onChange = (e) => {
    const newValue = e.target.value;
    if (!newValue) setAutocompleteText('');
    // eslint-disable-next-line
    else if (newValue.match(/^[0-9A-Za-z\s\-]+$/))
      setAutocompleteText(newValue);
  };

  const renderOption = (props, option) => {
    return (
      <Typography {...props} style={{ fontSize: '1.4rem' }}>
        {option}
      </Typography>
    );
  };

  const resetAutocomplete = () => {
    setSearchParams((prev) => ({
      ...prev,
      pilotAutocompleteId: '',
      pilotAutocompleteValue: ''
    }));
    setAutocompleteText('');
  };

  return sdoAdminEligibilityCriteriaContext.loading || !searchState.sdoDate ? (
    <Loader />
  ) : (
    <>
      <div className="sdo-admin-manage-requests-title-cntr">
        <span className="title">
          {t('specialDayOff.adminEligibilityCriteria.manageRequests.title')}
        </span>
        <Link
          to="/sdo-admin-report-details"
          className="sdo-admin-manage-requests__report-link"
        >
          <span className="view-reports-link">
            {t(
              'specialDayOff.adminEligibilityCriteria.manageRequests.viewRequests'
            )}
          </span>
        </Link>
      </div>
      <div className="sdo-admin-manage-requests-search-cntr">
        <div className="search__inputs-cntr">
          <Autocomplete
            open={autocompleteOpen}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') {
                if (value.length < 3) {
                  if (autocompleteOpen) setAutocompleteOpen(false);
                } else {
                  if (!autocompleteOpen) setAutocompleteOpen(true);
                }
              }
            }}
            inputValue={autocompleteText}
            onClose={() => setAutocompleteOpen(false)}
            className={autocompleteStyle.autocomplete}
            onChange={(event) => handleAutocompleteOnChange(event)}
            options={pilotsAutocompleteList}
            popupIcon={null}
            value={searchParams.pilotAutocompleteValue}
            renderOption={(props, option) => renderOption(props, option)}
            renderInput={(params) => (
              <TextField
                {...params}
                className={autocompleteStyle.textField}
                label="Pilot name or ID"
                onChange={onChange}
                InputLabelProps={{
                  className: autocompleteStyle.textFieldLabel
                }}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  inputProps: {
                    ...params.inputProps,
                    maxLength: 40,
                    style: { paddingLeft: 0 }
                  }
                }}
              />
            )}
          />
          <span className="inputs__lbl">
            {t('specialDayOff.adminEligibilityCriteria.manageRequests.or')}
          </span>
          <div className="select-cntr--date select-cntr--date-ml">
            <AppSelect
              fillElements={sdoDates}
              multiSelect={false}
              selectedItem={searchParams.sdoDate}
              selectLabel="For SDO in"
              selectRef={selects}
              resetSelect={resetSelects}
              onChange={() => resetAutocomplete()}
            />
          </div>
          <div className="select-cntr--pos select-cntr--pos-ml">
            <AppSelect
              fillElements={sdoPositions}
              multiSelect={false}
              selectedItem={searchParams.sdoPosition}
              selectLabel="Position"
              selectRef={selects}
              resetSelect={resetSelects}
              onChange={() => resetAutocomplete()}
            />
          </div>
        </div>
        <div className="search-buttons search-buttons--marginLeft">
          <Button
            variant="outlined"
            className="search-btn btn--width"
            onClick={handleSearchClick}
          >
            {t('SearchBar.searchButton')}
          </Button>
          <Button
            variant="outlined"
            className="cancel-btn btn--width"
            onClick={handleClearClick}
          >
            {t('SearchBar.clearButton')}
          </Button>
        </div>
      </div>
      {error && (
        <div className="notification">
          {' '}
          <i className="icon-circle-flight"></i>
          {t('specialDayOff.adminEligibilityCriteria.manageRequests.error')}
        </div>
      )}
      <div className="sdo-manage-requests-multisort-instruction">
        {t('dbm.multiSortInstruction')}
      </div>
      <div className="sdo-manage-requests-table-cntr">
        <DataGridPro
          autoHeight
          columns={columns}
          disableColumnMenu
          disableRowSelectionOnClick
          //disableExtendRowFullWidth={true}
          //disableSelectionOnClick
          // onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          // pageSize={pageSize}
          // initialState={{
          //     pagination: {
          //       paginationModel: paginationModel,
          //     },
          // }}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={rowsPerPageOptions}
          pagination
          slots={{
            pagination: (props: any) => (
              <>
                <div
                  className={`view-all-${getNumDigits(paginationModel.pageSize)}`}
                >
                  {paginationModel.pageSize < rows.length ? (
                    <div className="enabled-view-all">
                      <span
                        onClick={() =>
                          showAllRows(
                            setViewAllClicked,
                            rows.length,
                            paginationModel,
                            setPaginationModel
                          )
                        }
                      >
                        View All
                      </span>
                    </div>
                  ) : rows.length > 10 ? (
                    <div className="disabled-view-all">
                      <span>View All</span>
                    </div>
                  ) : (
                    <div className="disabled-view-all-adjusted">
                      <span>View All</span>
                    </div>
                  )}
                </div>
                <GridPagination className="pagination-grid" />
              </>
            )
          }}
          rows={rows}
          sortModel={sortModel}
          onSortModelChange={(model) => handleSortChange(model)}
        />
      </div>
    </>
  );
};

export default SdoAdminManageRequests;
